import React, { useEffect, useRef, useState } from "react";
import { matchRoutes, useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import BarekamText from "../../pages/components/text";
import { ActionBar } from "../../pages/components/action-bar/action-bar";
import { BarekamIcon, IconVariants } from "../../pages/components/icon/barekam-icon";
import { ActionbarType } from "../../redux/reducers/actionbar-reducers";
import { StandardDialog } from "../../pages/components/dialogs/standard-dialog/standard-dialog";
import { changeState, getOrders, getOrdersStatesSummary } from "../../redux/slices/orders-slice";
import { OutletContext } from "../../router/router";
import { BarekamColumns, OrdersTableColumn, WarehouseWorkerColumns } from "../../pages/orders/my/models/column-models";
import { OrderViewEnum } from "../../pages/orders/enums/order/view.enum";
import OrderList from "../../pages/orders/my/order-list/order-list";
import { OrderModel } from "../../pages/shared/models/order/order-item.model";
import { changeSidebarInOrders, changeSidebarWithNavigate } from "../../redux/slices/sidebar.slice";
import { Message, MessageSections } from "../../pages/components/message/message";
import { changeActionbarType } from "../../redux/slices/actionbar.slice";
import AddProduct from "../actions/dialogs/add-product/add-product";
import {
    BulkActionPopupActionTypes,
    GenericActionTypes,
    InWarehouseActionTypes,
    ReadyForShipmentActionTypes
} from "../actions/action-types.enum";
import { ActionDialog } from "../actions/dialogs/action-dialog";
import { ActionsByState } from "../actions/actions-by-state";
import Template from "../template-generator/Template";
import { useReactToPrint } from "react-to-print";
import ordersService from "../../services/orders.service";
import { getOrdersList } from "../../redux/slices/crm.slice";
import GroupCards from "./group-cards/group-cards";
import { OrderStatesEnum } from "../../pages/orders/enums/order-states.enum";
import { LocationTable } from "./location-table/location-table";
import { OrdersResponseModel } from "../../pages/shared/models/api/orders-response.model";
import { OrderPaymentStatusNames } from '../../actions/types';
import { getOrderStates } from "../../redux/slices/static-data.slice";

const internalTrackingNumberColumn: OrdersTableColumn = { field: 'internalTrackingNumber', headerName: 'order_list_barekam_tracking', variant: BarekamColumns.barekamTrack };
const productTrackNumbersColumn: OrdersTableColumn = { field: 'products', headerName: 'order_list_tracking', variant: BarekamColumns.productTrackNumbers };
const nameColumn: OrdersTableColumn = { field: 'name', headerName: 'order_list_name', variant: BarekamColumns.text };
const senderColumn: OrdersTableColumn = { field: 'shippingInfo', headerName: 'order_list_sender', variant: BarekamColumns.sender, };
const shippingCategoryColumn: OrdersTableColumn = { field: 'shippingInfo', headerName: 'order_list_shipping_category', variant: BarekamColumns.shippingCategory, };
const paymentInfoColumn: OrdersTableColumn = { field: 'paymentInfo', headerName: 'order_list_price', variant: BarekamColumns.money, };
const locationColumn: OrdersTableColumn = { field: '', headerName: 'order_list_location', variant: WarehouseWorkerColumns.location };
const paymentStatusColumn: OrdersTableColumn = { field: 'paymentStatus', headerName: 'order_list_payment', variant: BarekamColumns.paymentStatus, };
const recipientColumn: OrdersTableColumn = { field: 'recipient', headerName: 'order_list_recipient', variant: BarekamColumns.recipient, };
const weightColumn: OrdersTableColumn = { field: 'weight', headerName: 'order_list_weight', variant: BarekamColumns.weight, };
const productWeightColumn: OrdersTableColumn = { field: 'products', headerName: 'order_list_weight', variant: BarekamColumns.productWeight, };
const productPriceColumn: OrdersTableColumn = { field: 'products', headerName: 'order_list_product_price', variant: BarekamColumns.productPrice, };
const lastActionDate: OrdersTableColumn = { field: 'trackRecord', headerName: 'order_last_action_date', variant: BarekamColumns.lastActionDate, }
const inWarehouseActionsColumn: OrdersTableColumn = { field: '', headerName: 'order_list_actions', variant: WarehouseWorkerColumns.inWarehouseActions, }
const listColumn: OrdersTableColumn = { field: '', headerName: 'order_list_crm_list', variant: WarehouseWorkerColumns.list, }
const groupColumn: OrdersTableColumn = { field: '', headerName: 'order_list_group', variant: WarehouseWorkerColumns.group, }
const readyForShipmentActionsColumn: OrdersTableColumn = { field: '', headerName: 'order_list_actions', variant: WarehouseWorkerColumns.readyForShipmentActions, }
const inLocalWarehouseActionsColumn: OrdersTableColumn = { field: '', headerName: 'order_list_actions', variant: WarehouseWorkerColumns.inLocalWarehouseActions, }
const onTheWayActionsColumn: OrdersTableColumn = { field: '', headerName: 'order_list_actions', variant: WarehouseWorkerColumns.onTheWayActions, }
const unidentifiedActionsColumn: OrdersTableColumn = { field: '', headerName: 'order_list_actions', variant: WarehouseWorkerColumns.unidentifiedActions, }
const readyForPickupActionsColumn: OrdersTableColumn = { field: '', headerName: 'order_list_actions', variant: WarehouseWorkerColumns.readyForPickupActions, }

function OrderContent({ orderViewType, ordersData, onOpen, onAction, columns }: OrderContentProps) {
    switch (orderViewType) {
        case OrderViewEnum.list:
            return <OrderList ordersData={ordersData}
                openAction={onOpen}
                onAction={onAction}
                columns={columns}
                isWarehouseWorker={true} />
        case OrderViewEnum.cards:
            return <GroupCards />
        case OrderViewEnum.location:
            return <LocationTable orders={ordersData.orders} />
        default:
            return <></>
    }
}

export const WarehouseOrders = () => {
    const [isReady, setIsReady] = useState<boolean>(false);
    const [columns, setColumns] = useState<OrdersTableColumn[]>([]);

    const [activeAction, setActiveAction] = useState<string>();
    const [activeOrders, setActiveOrders] = useState<OrderModel[]>();
    const [addProductOpen, setAddProductOpen] = useState(false);
    const [isOrderPopupOpened, setIsOrderPopupOpened] = useState(false);
    const [orderToPrint, setOrderToPrint] = useState<OrderModel>();

    const { ordersData, orderViewType } = useAppSelector(state => state.ordersReducer);
    const { orderStateId } = useAppSelector(state => state.ordersReducer);
    const { orderStates } = useAppSelector(state => state.staticDataReducer);
    const { languageId } = useAppSelector(state => state.messagesReducer);

    const outletContext = useOutletContext<OutletContext>();

    const componentRef = useRef<HTMLDivElement>(null);
    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        onAfterPrint: async () => {
            if (orderToPrint && orderToPrint.orderId) {
                ordersService.updateById({ isLabelPrinted: true }, orderToPrint.orderId, languageId)
                    .then(() => {
                        dispatch(getOrders({ page: ordersData.currentPage || 0 }));
                    })
                    .catch(reason => {
                        console.log(reason);
                    })
            }
        }
    });

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        orderToPrint && handlePrint()
    }, [orderToPrint])

    useEffect(() => {
        const matches = matchRoutes([{ path: '/:language/orders/:orderId' }], location);

        setIsOrderPopupOpened(matches
            ? matches.some(match => match.params.orderId ? !isNaN(+match.params.orderId) : false)
            : false
        )
    }, [location])

    useEffect(() => {
        switch (orderStateId) {
            case OrderStatesEnum.inWarehouse:
                setColumns([
                    internalTrackingNumberColumn,
                    nameColumn,
                    productTrackNumbersColumn,
                    recipientColumn,
                    weightColumn,
                    locationColumn,
                    paymentInfoColumn,
                    paymentStatusColumn,
                    lastActionDate,
                    inWarehouseActionsColumn
                ])
                break;
            case OrderStatesEnum.inIntermediateWarehouse:
                setColumns([
                    internalTrackingNumberColumn,
                    nameColumn,
                    productTrackNumbersColumn,
                    recipientColumn,
                    weightColumn,
                    locationColumn,
                    paymentInfoColumn,
                    paymentStatusColumn,
                    lastActionDate,
                    inWarehouseActionsColumn
                ])
                break;
            case OrderStatesEnum.readyForShipment:
                setColumns([
                    internalTrackingNumberColumn,
                    nameColumn,
                    recipientColumn,
                    weightColumn,
                    paymentInfoColumn,
                    paymentStatusColumn,
                    locationColumn,
                    listColumn,
                    groupColumn,
                    lastActionDate,
                    readyForShipmentActionsColumn
                ])
                break;
            case OrderStatesEnum.inLocalWarehouse:
                setColumns([
                    internalTrackingNumberColumn,
                    productTrackNumbersColumn,
                    nameColumn,
                    recipientColumn,
                    weightColumn,
                    locationColumn,
                    paymentInfoColumn,
                    paymentStatusColumn,
                    lastActionDate,
                    inLocalWarehouseActionsColumn
                ])
                break;
            case OrderStatesEnum.received:
                setColumns([
                    internalTrackingNumberColumn,
                    productTrackNumbersColumn,
                    nameColumn,
                    recipientColumn,
                    weightColumn,
                    paymentStatusColumn,
                    lastActionDate,
                    paymentInfoColumn,
                ])
                break;
            case OrderStatesEnum.toBeDelivered:
                setColumns([
                    internalTrackingNumberColumn,
                    productTrackNumbersColumn,
                    nameColumn,
                    recipientColumn,
                    weightColumn,
                    locationColumn,
                    paymentInfoColumn,
                    paymentStatusColumn,
                    lastActionDate,
                    onTheWayActionsColumn
                ])
                break;
            case OrderStatesEnum.onTheWay:
                setColumns([
                    internalTrackingNumberColumn,
                    productTrackNumbersColumn,
                    nameColumn,
                    recipientColumn,
                    weightColumn,
                    // locationColumn,
                    paymentInfoColumn,
                    paymentStatusColumn,
                    lastActionDate,
                    onTheWayActionsColumn
                ])
                break;
            case OrderStatesEnum.unidentified:
                setColumns([
                    internalTrackingNumberColumn,
                    productTrackNumbersColumn,
                    nameColumn,
                    weightColumn,
                    locationColumn,
                    paymentInfoColumn,
                    lastActionDate,
                    unidentifiedActionsColumn
                ])
                break;
            case OrderStatesEnum.entered:
                setColumns([
                    productTrackNumbersColumn,
                    nameColumn,
                    productWeightColumn,
                    productPriceColumn,
                    senderColumn,
                    lastActionDate
                ])
                break;
            case OrderStatesEnum.readyForPickup:
                setColumns([
                    internalTrackingNumberColumn,
                    productTrackNumbersColumn,
                    nameColumn,
                    weightColumn,
                    locationColumn,
                    paymentInfoColumn,
                    paymentStatusColumn,
                    paymentInfoColumn,
                    lastActionDate,
                    readyForPickupActionsColumn
                ])
                break;
            default:
                setColumns([
                    internalTrackingNumberColumn,
                    productTrackNumbersColumn,
                    nameColumn,
                    recipientColumn,
                    weightColumn,
                    locationColumn,
                    paymentStatusColumn,
                    paymentInfoColumn,
                    lastActionDate,
                ])
        }
    }, [orderStateId])

    useEffect(() => {
        dispatch(changeSidebarInOrders(true));
        dispatch(changeSidebarWithNavigate(false));
        dispatch(changeActionbarType(ActionbarType.ORDERS));

        if (!isReady) {
            Promise.allSettled([
                dispatch(getOrderStates()),
                dispatch(getOrders({ page: ordersData.currentPage || 0 })),
                dispatch(getOrdersList())
            ])
                .then(() => setIsReady(true));
        }
    }, [isReady])

    const ActionBarText = () => {
        const state = orderStates.find(state => state.orderStateId === orderStateId);

        return (
            !isOrderPopupOpened && state
                ? <Message messageKey={state.name} sectionId={MessageSections.ORDERS} />
                : <></>
        )
    }

    function onNewProductClick() {
        setAddProductOpen(true);
    }

    function onActionClick(actionId: string) {
        setActiveAction(actionId);
    }

    function onOpen(row: OrderModel) {
        navigate(`../${row.orderId}`)
    }

    function onAction(row: OrderModel, action: InWarehouseActionTypes | ReadyForShipmentActionTypes | BulkActionPopupActionTypes | GenericActionTypes) {
        switch (action) {
            case InWarehouseActionTypes.PRINT_BLANK:
                setOrderToPrint({ ...row });
                break;
            case InWarehouseActionTypes.CHANGE_STATUS:
            case InWarehouseActionTypes.LOCATE:
            case InWarehouseActionTypes.MARK_AS_READY_FOR_SHIPMENT:
                setActiveAction(action);
                setActiveOrders([row]);
                break;
            case GenericActionTypes.SEE_LOCATION_HISTORY:
                setActiveAction(action);
                setActiveOrders([row]);

                break;
            case GenericActionTypes.SEE_STATUS_HISTORY:
                setActiveAction(action);
                setActiveOrders([row]);

                break;
        }
    }

    function onAddProductClose() {
        setAddProductOpen(false);
        setTimeout(() => navigate('../management/warehouse-orders/my'), 1000);
        // setIsReady(false);
    }

    if (isReady) {
        return (
            <>
                {outletContext.isWarehouseWorker || outletContext.isLocalWarehouseWorker
                    ? <div style={{ display: 'none' }}>
                        <Template ref={componentRef} variant={OrderPaymentStatusNames.WAREHOUSE} order={orderToPrint} />
                    </div>
                    : ''
                }

                {outletContext.isWarehouseWorker ?
                    <ActionBar buttonText={<>
                        <BarekamIcon variant={IconVariants.plus} size={{ width: '1rem', height: '1rem' }} />

                        <BarekamText variant={'m'} fontWeight={700}>
                            <Message messageKey={'add_new_product'}
                                sectionId={MessageSections.ORDERS} />
                        </BarekamText>
                    </>}
                        headerText={<ActionBarText />}
                        buttonFunction={onNewProductClick}
                        actions={ActionsByState[orderStateId]}
                        actionFunction={onActionClick} />
                    : (outletContext.isLocalWarehouseWorker ?
                        <ActionBar
                            headerText={<ActionBarText />}
                            actions={ActionsByState[orderStateId]}
                            actionFunction={onActionClick} />
                        : '')
                }

                <OrderContent orderViewType={orderViewType}
                    ordersData={ordersData}
                    onOpen={onOpen}
                    onAction={onAction}
                    columns={columns} />

                <StandardDialog
                    headerLabel={
                        <BarekamText variant={'l'} bold>
                            <Message messageKey={'add_new_product'} sectionId={MessageSections.ORDERS} />
                        </BarekamText>}
                    open={addProductOpen}>
                    <AddProduct closePopup={() => onAddProductClose()} />
                </StandardDialog>

                <ActionDialog open={!!activeAction}
                    orderStateId={orderStateId}
                    closePopup={() => {
                        setActiveAction(undefined);
                        setActiveOrders(undefined);
                    }}
                    predefinedOrders={activeOrders}
                    historyDialogProps={activeOrders && activeOrders[0] && activeOrders[0].orderId
                        ? { category: 'Order', categoryId: activeOrders[0].orderId } : undefined
                    }
                    action={activeAction} />
            </>
        )
    } else {
        return <></>
    }
}

interface OrderContentProps {
    orderViewType: OrderViewEnum,
    ordersData: OrdersResponseModel,
    onOpen: (order: OrderModel, withEdit?: (boolean | undefined)) => void,
    onAction: (order: OrderModel, action: (InWarehouseActionTypes | ReadyForShipmentActionTypes | BulkActionPopupActionTypes)) => void,
    columns: OrdersTableColumn[]
}